<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useThemeSwitch } from '@/composables/useThemeSwitch'
import { useAuth } from '@/composables/useAuth'
import { useUserProfile } from '@/features/user/composables/useUserProfile'
import { useSidebar } from '@/composables/useSidebar'
import { useDatapoints } from '@/composables/useDatapoints'
import { useAiSearchRunning } from '@/composables/useAiSearch'
import { useSubscription } from '@/features/subscription/composables/useSubscription'
import { useUserCreditsStore } from '@/features/usercredits/stores/user-credits.store'
import CreditsDisplay from './CreditsDisplay.vue'
import { HelpButton } from '@/features/tour'
import { NotificationCenterMenu } from '@/features/notificationcenter'
import { ClassicViewButton } from '@/features/experience'

const route = useRoute()
const router = useRouter()
const { isDark, toggleTheme } = useThemeSwitch()
const { isAuthenticated } = useAuth()
const { credits, loading, isSalesUser } = useUserProfile()
const { isMobile, toggle: toggleSidebar } = useSidebar()
const { enableAiSearch } = useDatapoints()
// The search view works the query out (`?aiq=`); the button spins until it has.
const aiSearchRunning = useAiSearchRunning()

// "Add Credits" button (Vue 2 TopNavbar: canAddMoreCredits = !isOrganic || subscriptionIsPaid).
// A contract/paid account can always top up; an organic account only once its plan is paid.
const { isOrganic, subscriptionIsPaid } = useSubscription()
const canAddMoreCredits = computed(() => !isOrganic.value || subscriptionIsPaid.value)
const userCreditsStore = useUserCreditsStore()
function openAddCredits(): void {
	void userCreditsStore.openPurchaseDialog()
}

const searchQuery = ref('')
const searchFocused = ref(false)

// Rotating placeholder examples teach the two ways to use the AI search:
// a plain name/website lookup, or a natural-language prompt that gets
// translated into filters. Rotation pauses while the field is focused or typed in.
const AI_SEARCH_EXAMPLES = [
	'Search by name, website or industry…',
	'Try “retailers in the UK with turnover above £1m”',
	'Try “SaaS companies in Germany, 50–200 employees”',
	'Try “fashion brands that sell on Shopify”',
]
const exampleIndex = ref(0)
let rotateTimer: ReturnType<typeof setInterval> | undefined

const searchPlaceholder = computed(() => {
	if (!enableAiSearch.value) return 'Search by name, website or industry'
	return AI_SEARCH_EXAMPLES[exampleIndex.value]
})

onMounted(() => {
	rotateTimer = setInterval(() => {
		// Freeze the hint while the user is engaging with the field.
		if (!enableAiSearch.value || searchFocused.value || searchQuery.value) return
		exampleIndex.value = (exampleIndex.value + 1) % AI_SEARCH_EXAMPLES.length
	}, 3500)
})

onUnmounted(() => {
	if (rotateTimer) clearInterval(rotateTimer)
})

function onSearch(): void {
	const trimmed = searchQuery.value.trim()
	if (!trimmed || aiSearchRunning.value) return
	if (enableAiSearch.value) {
		// AI mode: hand the raw prompt to the seeded view (current view if it's a search
		// view, else List Builder). The destination classifies via `response_type` and
		// may hand the filters to the other view. The prompt stays in the box, as in Vue 2,
		// so it can be reworded if the AI could not use all of it.
		const seeded =
			route.name === 'ListBuilder' || route.name === 'PeopleSearch' ? route.name : 'ListBuilder'
		router.push({ name: seeded, query: { aiq: trimmed } })
		return
	}
	// Datapoint off / still loading: plain company-name/website search (Vue 2 parity).
	router.push({ path: '/listbuilder', query: { q: trimmed } })
	searchQuery.value = ''
}
</script>

<template>
	<div class="app-topbar d-flex align-center px-2 px-sm-4 py-2">
		<!-- Hamburger (mobile only) — opens the navigation drawer, which is otherwise
		     hidden below the md breakpoint. -->
		<v-btn
			v-if="isMobile"
			icon="fa-solid fa-bars"
			variant="text"
			size="small"
			class="app-topbar__menu-btn mr-2"
			aria-label="Open navigation menu"
			@click="toggleSidebar"
		/>

		<!-- TAMI logo (mobile only) -->
		<img
			v-if="isMobile"
			src="@/assets/logo.svg"
			alt="TAMI"
			height="22"
			class="app-topbar__mobile-logo mr-3"
			style="filter: brightness(0) saturate(100%)"
		/>

		<!-- Company search bar -->
		<div
			class="app-topbar__search d-none d-sm-flex align-center"
			:class="{ 'app-topbar__search--ai': enableAiSearch }"
		>
			<v-tooltip
				location="bottom"
				max-width="280"
				:disabled="!enableAiSearch"
			>
				<template #activator="{ props: tip }">
					<div
						v-bind="tip"
						class="app-topbar__search-label"
					>
						<i
							v-if="enableAiSearch"
							class="fa-solid fa-wand-magic-sparkles app-topbar__search-spark"
						/>
						<span>{{ enableAiSearch ? 'AI Search' : 'Find a company' }}</span>
					</div>
				</template>
				<span>
					Search by company name or website — or just describe who you're
					looking for, e.g. “retailers in the UK with turnover above £1m”, and
					AI will turn it into filters.
				</span>
			</v-tooltip>
			<div class="app-topbar__search-divider" />
			<input
				v-model="searchQuery"
				type="text"
				class="app-topbar__search-input"
				:placeholder="searchPlaceholder"
				@keyup.enter="onSearch"
				@focus="searchFocused = true"
				@blur="searchFocused = false"
			/>
			<v-btn
				color="primary"
				size="small"
				variant="flat"
				class="app-topbar__search-btn text-none"
				:loading="aiSearchRunning"
				data-test="topbar-search-btn"
				@click="onSearch"
			>
				<i
					v-if="enableAiSearch"
					class="fa-solid fa-wand-magic-sparkles mr-2"
				/>
				Search
			</v-btn>
		</div>

		<v-spacer />

		<!-- Credits + actions -->
		<template v-if="isAuthenticated">
			<CreditsDisplay
				:credits="credits"
				:is-sales-user="isSalesUser"
				:loading="loading"
				class="mr-2 d-none d-md-flex"
			/>

			<!-- Add Credits (Vue 2 parity: present in the top nav on every page, not only the
			     credits page). Navigates to the credits page where packages are purchased. -->
			<!-- Label collapses to the icon on phones (xs) so the action cluster fits the viewport;
			     the button stays reachable and primary. Full "Add Credits" label returns at sm+. -->
			<v-btn
				v-if="canAddMoreCredits"
				color="primary"
				variant="flat"
				size="small"
				class="text-none mr-1"
				data-test="add-credits-btn"
				:aria-label="'Add Credits'"
				@click="openAddCredits"
			>
				<i class="fa-solid fa-plus" />
				<span class="d-none d-sm-inline ml-2">Add Credits</span>
			</v-btn>

			<ClassicViewButton />

			<HelpButton />

			<NotificationCenterMenu />

			<v-tooltip
				:text="isDark ? 'Light mode' : 'Dark mode'"
				location="bottom"
			>
				<template #activator="{ props: tip }">
					<v-btn
						v-bind="tip"
						icon
						variant="text"
						size="small"
						class="mx-1"
						:aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
						@click="toggleTheme"
					>
						<Transition
							name="fade"
							mode="out-in"
						>
							<i
								:key="String(isDark)"
								:class="isDark ? 'fa-solid fa-sun' : 'fa-solid fa-moon'"
							/>
						</Transition>
					</v-btn>
				</template>
			</v-tooltip>
		</template>
	</div>
</template>

<style scoped lang="scss">
.app-topbar {
	min-height: 52px;
	border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	background-color: rgb(var(--v-theme-surface));
	position: sticky;
	top: 0;
	z-index: 2;

	&__mobile-logo {
		flex-shrink: 0;
	}

	&__search {
		height: 38px;
		border: 1px solid rgba(var(--v-border-color), 0.3);
		border-radius: 10px;
		overflow: hidden;
		background-color: rgb(var(--v-theme-surface));
		transition:
			border-color 200ms ease,
			max-width 200ms cubic-bezier(0.4, 0, 0.2, 1),
			box-shadow 200ms ease;
		max-width: 480px;
		flex-grow: 1;

		// Focus draws the search wider and glows: clear "you are typing here" cue
		&:focus-within {
			border-color: rgb(var(--v-theme-primary));
			box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.1);
			max-width: 560px;
		}
	}

	&__search--ai &__search-label {
		cursor: help;
	}

	&__search-label {
		display: flex;
		align-items: center;
		gap: 7px;
		height: 100%;
		font-size: 0.8125rem;
		font-weight: 600;
		color: rgb(var(--v-theme-on-surface));
		white-space: nowrap;
		padding: 0 14px;
		flex-shrink: 0;
		transition: background-color 200ms ease;
	}

	&__search-spark {
		font-size: 0.8125rem;
		line-height: 1;
	}

	&__search-divider {
		width: 1px;
		height: 20px;
		background-color: rgba(var(--v-border-color), 0.3);
		flex-shrink: 0;
	}

	&__search-input {
		flex-grow: 1;
		border: none;
		outline: none;
		font-size: 0.8125rem;
		padding: 0 12px;
		background: transparent;
		color: rgb(var(--v-theme-on-surface));
		min-width: 0;

		&::placeholder {
			color: rgba(var(--v-theme-on-surface), 0.45);
		}
	}

	&__search-btn {
		height: 100% !important;
		border-radius: 0 9px 9px 0;
		letter-spacing: 0;
		font-weight: 600;
		font-size: 0.8125rem;
		min-width: 80px;
	}
}
</style>
